import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { refreshSession, signOut } from 'apollo/auth'
import { useReactiveVar, useQuery } from '@apollo/client'
import { isUserActive } from 'apollo/variables'
import { minute } from 'constants/constants'
import config from 'constants/config'
import { getFromStorage } from 'utils/localStorage'
import { setToSessionStorage } from 'utils/sessionStorage'
import { Button } from 'ui/styled'
import { YELLOW_DARK } from 'ui/constants'
import CURRENT_USER from 'apollo/queries/CURRENT_USER'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { StyledModal, ModalContent, StyledMessage } from './styled'
import {
  OPEN_INACTIVITY_MODALS,
  CLOSE_INACTIVITY_MODALS,
  CLOSE_INACTIVITY_MODALS_AFTER_LOGOUT
} from './constants'

const { OKTA_REDIRECT, TIMEOUT1, TIMEOUT2 } = config

const timeout1 = TIMEOUT1 * minute
const timeout2 = TIMEOUT2 * minute

const inactivityBroadcastChannel = new BroadcastChannel('inactivity-broadcast')

const InactivityModal = () => {
  const active = useReactiveVar(isUserActive)
  const location = useLocation()
  const [isSignedOut, setIsSignedOut] = useState(false)
  const { refetch } = useQuery(CURRENT_USER, {
    fetchPolicy: 'cache-only'
  })

  const handleInactivityMessage = useCallback(
    event => {
      const type = event?.data?.type
      if (!type) return

      if (type === OPEN_INACTIVITY_MODALS) {
        isUserActive(false)
      }

      if (type === CLOSE_INACTIVITY_MODALS) {
        isUserActive(true)
      }

      if (type === CLOSE_INACTIVITY_MODALS_AFTER_LOGOUT) {
        refetch()
        isUserActive(true)
      }
    },
    [refetch]
  )

  useEffect(() => {
    inactivityBroadcastChannel.addEventListener(
      'message',
      handleInactivityMessage
    )

    return () => {
      inactivityBroadcastChannel.removeEventListener(
        'message',
        handleInactivityMessage
      )
    }
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      const inactivityTime = Date.now() - getFromStorage('lastUserActivityAt')()
      if (inactivityTime > timeout1 && inactivityTime < timeout1 + timeout2) {
        inactivityBroadcastChannel.postMessage({ type: OPEN_INACTIVITY_MODALS })
      }
      if (inactivityTime > timeout1 + timeout2) {
        const { pathname, search } = location
        setToSessionStorage('lastVisitedPage')(pathname + search)
        clearInterval(timer)
        setIsSignedOut(true)
        signOut(true)
      }
    }, minute)
    return () => clearInterval(timer)
  }, [active])

  const changeIdleStatus = () => {
    if (isSignedOut) {
      window.location.replace(OKTA_REDIRECT)
    } else {
      refreshSession(false, true)
    }
  }

  return (
    <StyledModal
      title="Inactivity Warning"
      centered
      open={!active}
      closable={false}
      footer={null}
    >
      <ModalContent>
        <ExclamationCircleOutlined
          style={{ fontSize: '34px', color: YELLOW_DARK }}
        />
        <StyledMessage>
          {isSignedOut
            ? 'You have been logged out due to inactivity'
            : 'You will be logged out due to inactivity in 15 minutes'}
        </StyledMessage>
        <Button
          width="100%"
          onClick={changeIdleStatus}
          btntype="primary"
          data-test="backOrSignInBtn"
        >
          {isSignedOut ? 'Sign back in' : 'I am back'}
        </Button>
      </ModalContent>
    </StyledModal>
  )
}

export default InactivityModal
